import { useState } from "react";
import {
    FEE_FIELD_LABELS,
    PRIORITY_FIELD_LABELS,
    PRIORITY_KEYS,
    PRIORITY_LABELS,
    PRODUCT_KEYS,
    PRODUCT_LABELS,
} from "./pricingMeta";
import { formatMoneyInput, handleMoneyInputChange } from "../productPackages/moneyInput";
import PublishConfirmModal from "./publishConfirmModal";

const fieldClass = "w-full rounded border border-primary/50 bg-black px-2 py-1 text-primary outline-none focus:border-primary";
const sectionClass = "rounded border border-primary/50 bg-mgs-bg p-4 text-left";
const checkboxClass = "h-4 w-4 accent-[#d59106]";

const PERCENT_PRIORITY_FIELDS = ["additionalRate", "discountPerItem", "maxDiscount"];
const INTEGER_PRIORITY_FIELDS = ["minDeadlineDays", "maxItems", "hubItemsPerCase"];
const PRIORITY_FIELD_ORDER = [
    "additionalRate",
    "daysPerItem",
    "discountPerItem",
    "maxDiscount",
    "minDeadlineDays",
    "maxItems",
    "hubItemsPerCase",
];

const toPercentDisplay = (rate) => Number(((Number(rate) || 0) * 100).toFixed(4));
const fromPercentInput = (raw) => Number(((Number(raw) || 0) / 100).toFixed(6));

const PricingEditor = ({
    draft,
    diff,
    isDirty,
    validationErrors,
    setProductField,
    setPriorityField,
    setFeeField,
    toggleProductActive,
    togglePriorityActive,
    publish,
}) => {
    const [showPublishModal, setShowPublishModal] = useState(false);

    if (!draft) {
        return null;
    }

    const publishDisabled = !isDirty || validationErrors.length > 0;

    return (
        <div className="space-y-4">
            <section className={sectionClass}>
                <h2 className="mb-3 text-xl font-semibold text-[#d59106]">Produtos</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-[#d59106]/40 text-left text-sm">
                        <thead className="bg-[#111] text-[#d59106]">
                            <tr>
                                <th className="px-3 py-2">Nome</th>
                                <th className="px-3 py-2">Preço base</th>
                                <th className="px-3 py-2" title="Adicional cobrado se o cliente do pré-grading decidir lacrar">Preço do lacre</th>
                                <th className="px-3 py-2">Ativo</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#d59106]/20">
                            {PRODUCT_KEYS.map((key) => {
                                const product = draft.products?.[key] || {};
                                const label = PRODUCT_LABELS[key] || key;
                                return (
                                    <tr key={key}>
                                        <td className="px-3 py-2 font-semibold text-primary">{label}</td>
                                        <td className="px-3 py-2">
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                className={fieldClass}
                                                value={formatMoneyInput(product.baseCents)}
                                                onChange={(event) => handleMoneyInputChange(
                                                    event.target.value,
                                                    (cents) => setProductField(key, "baseCents", cents)
                                                )}
                                            />
                                        </td>
                                        <td className="px-3 py-2">
                                            {key === "pre" ? (
                                                <div className="flex flex-col gap-1">
                                                    <label className="text-xs text-primary/70">
                                                        Com subgrades
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            className={fieldClass}
                                                            value={formatMoneyInput(product.preLacre?.withSubgradesCents)}
                                                            onChange={(event) => handleMoneyInputChange(
                                                                event.target.value,
                                                                (cents) => setProductField(key, "preLacre.withSubgradesCents", cents)
                                                            )}
                                                        />
                                                    </label>
                                                    <label className="text-xs text-primary/70">
                                                        Sem subgrades
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            className={fieldClass}
                                                            value={formatMoneyInput(product.preLacre?.withoutSubgradesCents)}
                                                            onChange={(event) => handleMoneyInputChange(
                                                                event.target.value,
                                                                (cents) => setProductField(key, "preLacre.withoutSubgradesCents", cents)
                                                            )}
                                                        />
                                                    </label>
                                                </div>
                                            ) : (
                                                <span className="text-primary/40">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            <input
                                                type="checkbox"
                                                className={checkboxClass}
                                                aria-label={`Ativo: ${label}`}
                                                checked={Boolean(product.active)}
                                                onChange={() => toggleProductActive(key)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className={sectionClass}>
                <h2 className="mb-3 text-xl font-semibold text-[#d59106]">Prioridades</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-[#d59106]/40 text-left text-sm">
                        <thead className="bg-[#111] text-[#d59106]">
                            <tr>
                                <th className="px-3 py-2">Nome</th>
                                {PRIORITY_FIELD_ORDER.map((field) => (
                                    <th key={field} className="px-3 py-2">
                                        {PRIORITY_FIELD_LABELS[field]}
                                        {PERCENT_PRIORITY_FIELDS.includes(field) ? " (%)" : ""}
                                    </th>
                                ))}
                                <th className="px-3 py-2">Ativo</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#d59106]/20">
                            {PRIORITY_KEYS.map((key) => {
                                const priority = draft.priorities?.[key] || {};
                                const label = PRIORITY_LABELS[key] || key;
                                return (
                                    <tr key={key}>
                                        <td className="px-3 py-2 font-semibold text-primary">{label}</td>
                                        {PRIORITY_FIELD_ORDER.map((field) => {
                                            const isPercent = PERCENT_PRIORITY_FIELDS.includes(field);
                                            const isInteger = INTEGER_PRIORITY_FIELDS.includes(field);
                                            const value = isPercent
                                                ? toPercentDisplay(priority[field])
                                                : priority[field] ?? 0;
                                            return (
                                                <td key={field} className="px-3 py-2">
                                                    <input
                                                        type="number"
                                                        step={isInteger ? "1" : "any"}
                                                        className={fieldClass}
                                                        aria-label={`${PRIORITY_FIELD_LABELS[field]}: ${label}`}
                                                        value={value}
                                                        onChange={(event) => {
                                                            if (isPercent) {
                                                                setPriorityField(key, field, fromPercentInput(event.target.value));
                                                                return;
                                                            }
                                                            const parsed = isInteger
                                                                ? parseInt(event.target.value, 10)
                                                                : parseFloat(event.target.value);
                                                            setPriorityField(key, field, Number.isNaN(parsed) ? 0 : parsed);
                                                        }}
                                                    />
                                                </td>
                                            );
                                        })}
                                        <td className="px-3 py-2">
                                            <input
                                                type="checkbox"
                                                className={checkboxClass}
                                                aria-label={`Ativo: ${label}`}
                                                checked={Boolean(priority.active)}
                                                onChange={() => togglePriorityActive(key)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className={sectionClass}>
                <h2 className="mb-3 text-xl font-semibold text-[#d59106]">Taxas</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="text-sm font-semibold text-primary">
                        {FEE_FIELD_LABELS.handlingFeeRate} (%)
                        <input
                            type="number"
                            step="any"
                            className={fieldClass}
                            value={toPercentDisplay(draft.fees?.handlingFeeRate)}
                            onChange={(event) => setFeeField("handlingFeeRate", fromPercentInput(event.target.value))}
                        />
                    </label>
                    <label className="text-sm font-semibold text-primary">
                        {FEE_FIELD_LABELS.autographCents}
                        <input
                            type="text"
                            inputMode="numeric"
                            className={fieldClass}
                            value={formatMoneyInput(draft.fees?.autographCents)}
                            onChange={(event) => handleMoneyInputChange(
                                event.target.value,
                                (cents) => setFeeField("autographCents", cents)
                            )}
                        />
                    </label>
                </div>
            </section>

            {validationErrors.length > 0 ? (
                <ul className="rounded border border-red-500 bg-red-950/40 p-3 text-sm text-red-300">
                    {validationErrors.map((error) => (
                        <li key={error}>{error}</li>
                    ))}
                </ul>
            ) : null}

            <div className="flex justify-end">
                <button
                    type="button"
                    className="rounded bg-[#d59106] px-4 py-2 font-semibold text-black hover:bg-[#b47c06] disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={publishDisabled}
                    onClick={() => setShowPublishModal(true)}
                >
                    Publicar nova versão
                </button>
            </div>

            {showPublishModal ? (
                <PublishConfirmModal
                    diff={diff}
                    publish={publish}
                    onClose={() => setShowPublishModal(false)}
                />
            ) : null}
        </div>
    );
};

export default PricingEditor;
